import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from './client';
import type { ProjectDto, ThroughputWeekDto } from '../types/api';

export const projectKeys = {
  all:        ()          => ['projects']                      as const,
  detail:     (id: string) => ['projects', id]                 as const,
  throughput: (id: string) => ['projects', id, 'throughput']   as const,
};

export function useProjectList() {
  return useQuery({
    queryKey: projectKeys.all(),
    queryFn: () => client.get<ProjectDto[]>('/projects').then((r) => r.data),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => client.get<ProjectDto>(`/projects/${id}`).then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      client.post<ProjectDto>('/projects', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.all() }),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.delete(`/projects/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: projectKeys.all() }),
  });
}

export function useUpdateProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; description?: string; archive?: boolean }) =>
      client.patch<ProjectDto>(`/projects/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: projectKeys.detail(id) });
      qc.invalidateQueries({ queryKey: projectKeys.all() });
    },
  });
}

export function useProjectThroughput(id: string) {
  return useQuery({
    queryKey: projectKeys.throughput(id),
    queryFn: () => client.get<ThroughputWeekDto[]>(`/projects/${id}/throughput`).then((r) => r.data),
    enabled: !!id,
  });
}
